import { useCallback, useEffect, useRef, useState, type MutableRefObject } from "react";

/**
 * useAudioRecorder — ciclo de vida de gravação de áudio no browser (MediaStream +
 * MediaRecorder), com escolha de formato, medição de duração, tratamento de
 * permissão/dispositivo e limpeza rigorosa de recursos.
 *
 * Story F1 (feature "notas de voz"). Hook PURO: não faz upload nem conhece
 * storage/chat/atendimentos — o `<VoiceRecorder>` consome este hook e entrega o
 * áudio ao pai. `getUserMedia` é pedido no GESTO do usuário (via `start()`), não
 * no mount — melhor UX de permissão + privacidade (sem mic "quente").
 */

export type RecorderStatus =
  | "idle" // pronto pra gravar
  | "requesting" // getUserMedia em voo
  | "recording"
  | "stopped" // tem resultado, aguardando enviar/descartar (preview)
  | "denied" // permissão negada
  | "no-device" // sem microfone / não suportado
  | "error";

export interface AudioRecording {
  blob: Blob;
  mimeType: string;
  durationSec: number;
  sizeBytes: number;
  /** object URL p/ tocar o preview — revogado em reset/cancel/unmount. */
  url: string;
}

export interface UseAudioRecorderOptions {
  /** teto de duração; ao atingir, para sozinho (default 180s = 3 min). */
  maxDurationSec?: number;
  /** disparado quando o teto força o auto-stop (o componente mostra o aviso). */
  onMaxDuration?: () => void;
}

export interface UseAudioRecorderResult {
  status: RecorderStatus;
  durationSec: number;
  error: string | null;
  recording: AudioRecording | null;
  isSupported: boolean;
  /** AnalyserNode ativo durante a gravação — o componente lê p/ desenhar o waveform. */
  analyserRef: MutableRefObject<AnalyserNode | null>;
  start: () => Promise<void>;
  stop: () => void;
  cancel: () => void;
  reset: () => void;
}

// Preferência de formato: opus é leve e amplamente suportado (Chrome/FF);
// mp4/aac é o fallback do Safari/iOS, que não grava webm.
const PREFERRED_MIME_TYPES = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/mpeg"];

function pickMimeType(): string {
  if (typeof MediaRecorder === "undefined" || !MediaRecorder.isTypeSupported) {
    return "";
  }
  for (const t of PREFERRED_MIME_TYPES) {
    if (MediaRecorder.isTypeSupported(t)) return t;
  }
  return "";
}

function detectSupport(): boolean {
  return (
    typeof navigator !== "undefined" &&
    !!navigator.mediaDevices &&
    typeof navigator.mediaDevices.getUserMedia === "function" &&
    typeof MediaRecorder !== "undefined"
  );
}

export function useAudioRecorder(options: UseAudioRecorderOptions = {}): UseAudioRecorderResult {
  const { maxDurationSec = 180, onMaxDuration } = options;

  const isSupported = detectSupport();
  const [status, setStatus] = useState<RecorderStatus>("idle");
  const [durationSec, setDurationSec] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [recording, setRecording] = useState<AudioRecording | null>(null);

  // Refs mutáveis (não disparam render).
  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const timerRef = useRef<number | null>(null);
  const startedAtRef = useRef<number>(0);
  const mimeTypeRef = useRef<string>("");
  const cancelledRef = useRef<boolean>(false); // descartar o resultado no onstop
  const startingRef = useRef<boolean>(false); // guard de re-entrância do start()
  const recordingUrlRef = useRef<string | null>(null); // p/ revogar sem depender do state
  const onMaxDurationRef = useRef(onMaxDuration);
  const maxDurationRef = useRef(maxDurationSec);

  useEffect(() => {
    onMaxDurationRef.current = onMaxDuration;
    maxDurationRef.current = maxDurationSec;
  }, [onMaxDuration, maxDurationSec]);

  // ---- helpers de teardown ----
  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  const closeAudioGraph = useCallback(() => {
    analyserRef.current = null;
    const ctx = audioContextRef.current;
    audioContextRef.current = null;
    if (ctx && ctx.state !== "closed") {
      void ctx.close().catch(() => undefined);
    }
  }, []);

  const revokePreview = useCallback(() => {
    if (recordingUrlRef.current) {
      URL.revokeObjectURL(recordingUrlRef.current);
      recordingUrlRef.current = null;
    }
  }, []);

  const start = useCallback(async () => {
    if (!isSupported) {
      setStatus("no-device");
      setError("Gravação de áudio não é suportada neste navegador.");
      return;
    }
    // Guard de re-entrância por REF (não pelo `status` do closure, que pode
    // estar defasado entre o setStatus e o próximo commit) → evita dois
    // getUserMedia concorrentes de gestos rápidos.
    if (startingRef.current || recorderRef.current) {
      return;
    }
    startingRef.current = true;

    try {
      revokePreview();
      setRecording(null);
      setError(null);
      setDurationSec(0);
      cancelledRef.current = false;
      setStatus("requesting");

      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch (err) {
        const name = (err as DOMException)?.name;
        if (name === "NotAllowedError" || name === "SecurityError") {
          setStatus("denied");
          setError("Permissão de microfone negada.");
        } else if (
          name === "NotFoundError" ||
          name === "DevicesNotFoundError" ||
          name === "OverconstrainedError"
        ) {
          setStatus("no-device");
          setError("Nenhum microfone encontrado.");
        } else {
          setStatus("error");
          setError("Não foi possível acessar o microfone.");
        }
        return;
      }

      // Abort: se cancel() ou o unmount rodou DURANTE o await do getUserMedia,
      // o teardown não teve o que limpar (refs ainda nulos). Desliga o stream
      // recém-obtido e sai — sem religar o mic nem re-entrar em "recording".
      if (cancelledRef.current) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }

      streamRef.current = stream;

      // Grafo Web Audio p/ o waveform (best-effort — se falhar, grava sem visual).
      try {
        const AudioCtx =
          window.AudioContext ||
          (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
        const ctx = new AudioCtx();
        audioContextRef.current = ctx;
        const source = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 1024;
        source.connect(analyser);
        analyserRef.current = analyser;
      } catch {
        analyserRef.current = null;
      }

      const mimeType = pickMimeType();
      mimeTypeRef.current = mimeType;
      let recorder: MediaRecorder;
      try {
        recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      } catch {
        setStatus("error");
        setError("Não foi possível iniciar a gravação.");
        stopStream();
        closeAudioGraph();
        return;
      }
      recorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (e: BlobEvent) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onerror = () => {
        // ex.: track revogada / hardware sumiu no meio da gravação.
        clearTimer();
        stopStream();
        closeAudioGraph();
        recorderRef.current = null;
        if (!cancelledRef.current) {
          setStatus("error");
          setError("A gravação foi interrompida.");
        }
      };

      recorder.onstop = () => {
        clearTimer();
        const finalDuration = startedAtRef.current ? (Date.now() - startedAtRef.current) / 1000 : 0;
        stopStream();
        closeAudioGraph();
        const type = recorder.mimeType || mimeTypeRef.current || "audio/webm";
        const blob = new Blob(chunksRef.current, { type });
        chunksRef.current = [];
        recorderRef.current = null;

        if (cancelledRef.current) {
          setDurationSec(0);
          setStatus("idle");
          return;
        }
        const url = URL.createObjectURL(blob);
        recordingUrlRef.current = url;
        setRecording({
          blob,
          mimeType: type,
          durationSec: Math.max(0, Math.round(finalDuration * 10) / 10),
          sizeBytes: blob.size,
          url,
        });
        setDurationSec(finalDuration);
        setStatus("stopped");
      };

      startedAtRef.current = Date.now();
      try {
        recorder.start();
      } catch {
        setStatus("error");
        setError("Não foi possível iniciar a gravação.");
        stopStream();
        closeAudioGraph();
        recorderRef.current = null;
        return;
      }
      setStatus("recording");

      timerRef.current = window.setInterval(() => {
        const secs = (Date.now() - startedAtRef.current) / 1000;
        setDurationSec(secs);
        if (secs >= maxDurationRef.current) {
          clearTimer(); // impede re-disparo do callback/toast até o onstop rodar
          onMaxDurationRef.current?.();
          if (recorderRef.current && recorderRef.current.state !== "inactive") {
            recorderRef.current.stop(); // onstop finaliza (vai pra preview)
          }
        }
      }, 200);
    } finally {
      startingRef.current = false;
    }
  }, [isSupported, revokePreview, stopStream, closeAudioGraph, clearTimer]);

  const stop = useCallback(() => {
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      cancelledRef.current = false;
      recorder.stop(); // onstop monta o resultado
    }
  }, []);

  const cancel = useCallback(() => {
    cancelledRef.current = true;
    const recorder = recorderRef.current;
    clearTimer();
    if (recorder && recorder.state !== "inactive") {
      recorder.stop(); // onstop vê cancelled → descarta + idle
    } else {
      stopStream();
      closeAudioGraph();
      recorderRef.current = null;
      revokePreview();
      setRecording(null);
      setDurationSec(0);
      setStatus("idle");
    }
    setError(null);
  }, [clearTimer, stopStream, closeAudioGraph, revokePreview]);

  const reset = useCallback(() => {
    revokePreview();
    setRecording(null);
    setDurationSec(0);
    setError(null);
    setStatus((s) => (s === "stopped" ? "idle" : s));
  }, [revokePreview]);

  // Mount: checagem best-effort de dispositivo + teardown total no unmount.
  useEffect(() => {
    if (!isSupported) {
      setStatus((s) => (s === "idle" ? "no-device" : s));
    } else if (navigator.mediaDevices.enumerateDevices) {
      let cancelledCheck = false;
      navigator.mediaDevices
        .enumerateDevices()
        .then((devices) => {
          if (cancelledCheck) return;
          const hasAudioIn = devices.some((d) => d.kind === "audioinput");
          // Só conclui "sem mic" se a lista veio populada E sem audioinput.
          // Lista vazia (ex. Safari antes de permissão) NÃO deriva "no-device".
          if (devices.length > 0 && !hasAudioIn) {
            setStatus((s) => (s === "idle" ? "no-device" : s));
          }
        })
        .catch(() => undefined);
      return () => {
        cancelledCheck = true;
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSupported]);

  useEffect(() => {
    return () => {
      // Teardown total no unmount.
      clearTimer();
      cancelledRef.current = true;
      const rec = recorderRef.current;
      if (rec && rec.state !== "inactive") {
        try {
          rec.stop();
        } catch {
          /* noop */
        }
      }
      recorderRef.current = null;
      stopStream();
      closeAudioGraph();
      revokePreview();
    };
  }, [clearTimer, stopStream, closeAudioGraph, revokePreview]);

  return {
    status,
    durationSec,
    error,
    recording,
    isSupported,
    analyserRef,
    start,
    stop,
    cancel,
    reset,
  };
}
